import { useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { AreaTexto } from '@/shared/ui/Formulario/AreaTexto'
import { Modal } from '@/shared/ui/Modal/Modal'

import { useAnularCotizacion } from './useBorrador'

type PropsAnular = {
  abierto: boolean
  cotizacionId: string
  numero: string | null
  onCerrar: () => void
  onAnulada?: () => void
}

/**
 * Anular una cotización.
 *
 * Deja el documento anulado en World Office y devuelve el trabajo a la fila.
 * El motivo es obligatorio y lo exige la base: una anulación sin explicación
 * no sirve de nada seis meses después, que es justo cuando se consulta.
 */
export function AnularCotizacion({
  abierto,
  cotizacionId,
  numero,
  onCerrar,
  onAnulada,
}: PropsAnular) {
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const anular = useAnularCotizacion()

  async function confirmar() {
    setError(null)

    try {
      await anular.mutateAsync({ cotizacionId, motivo })
      setMotivo('')
      onAnulada?.()
      onCerrar()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible anular.')
    }
  }

  return (
    <Modal
      abierto={abierto}
      titulo={numero ? `Anular ${numero}` : 'Anular cotización'}
      onCerrar={onCerrar}
      pie={
        <>
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="peligro"
            onClick={() => void confirmar()}
            trabajando={anular.isPending}
            disabled={motivo.trim().length === 0}
          >
            Anular
          </Boton>
        </>
      }
    >
      {error && (
        <div style={{ marginBottom: 'var(--e-4)' }}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}

      <div style={{ marginBottom: 'var(--e-4)' }}>
        <Aviso tipo="alerta">
          El documento queda anulado en World Office y el trabajo vuelve a quedar pendiente de
          cotizar. Después habrá que cotizarlo de nuevo, con otro número.
        </Aviso>
      </div>

      <AreaTexto
        etiqueta="Por qué se anula"
        obligatorio
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        ayuda="Queda en la historia del documento, con su nombre y la fecha."
        autoFocus
      />
    </Modal>
  )
}
